import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Chip,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Pagination, Skeleton,
} from '@mui/material';
import { CalendarToday, Person, Tag, Visibility } from '@mui/icons-material';
import { generateClient } from 'aws-amplify/api';
import { getUrl } from 'aws-amplify/storage';
import { listBlogs, listBlogTags } from '../../graphql/queries';
import { useNavigate } from 'react-router-dom';
import { surface, brand, ink, line } from '../../theme/tokens';
import Seo from '../../components/Common/Seo';

// One rhythm for every section heading on the page: a mustard rule, then the
// title. Keeps Featured / All Posts reading as siblings.
const sectionHeadingSx = {
  color: ink.primary,
  fontWeight: 700,
  fontSize: { xs: '1.5rem', md: '1.75rem' },
  letterSpacing: '-0.01em',
  pl: 2,
  borderLeft: `4px solid ${brand.mustard}`,
  mb: 3,
};

const cardSx = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  cursor: 'pointer',
  border: `1px solid ${line.subtle}`,
  // column layout lets CardContent grow so every "Read More" lines up
  '& .MuiCardMedia-root': { flexShrink: 0 },
};

const cardTitleSx = {
  color: ink.primary,
  fontWeight: 700,
  fontSize: '1.0625rem',
  lineHeight: 1.4,
  mb: 1,
};

// meta row: dates, view counts, read time -- ink.muted is the lightest text
// allowed on a card (5.17:1)
const metaSx = { color: ink.muted, fontSize: '0.75rem' };
const metaIconSx = { fontSize: 16, color: ink.muted };

const client = generateClient();

const BlogPage = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [sortBy, setSortBy] = useState('latest');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const blogsPerPage = 12;

  useEffect(() => {
    fetchBlogsAndTags();
  }, []);

  useEffect(() => {
    filterAndSortBlogs();
  }, [blogs, searchTerm, selectedTag, sortBy]);

  const fetchBlogsAndTags = async () => {
    try {
      setLoading(true);
      
      // Fetch published blogs
      const blogResponse = await client.graphql({
        query: listBlogs,
        variables: {
          filter: {
            status: { eq: 'PUBLISHED' }
          }
        }
      });
      
      // Fetch all tags
      const tagResponse = await client.graphql({
        query: listBlogTags
      });
      
      const blogsData = blogResponse.data.listBlogs.items;
      
      // Process blogs and get thumbnail URLs
      const processedBlogs = await Promise.all(
        blogsData.map(async (blog) => {
          console.log('Processing blog:', blog.title, 'thumbnail:', blog.thumbnailImage);
          let thumbnailUrl = '';
          if (blog.thumbnailImage) {
            try {
              if (blog.thumbnailImage.startsWith('http')) {
                thumbnailUrl = blog.thumbnailImage;
              } else {
                const url = await getUrl({ key: blog.thumbnailImage });
                thumbnailUrl = url.url.toString();
              }
              console.log('Generated thumbnail URL:', thumbnailUrl);
            } catch (error) {
              console.error('Error getting thumbnail URL for blog', blog.title, ':', error);
            }
          } else {
            console.log('No thumbnail image for blog:', blog.title);
          }
          
          return {
            ...blog,
            thumbnailDisplayUrl: thumbnailUrl
          };
        })
      );
      
      setBlogs(processedBlogs);
      setTags(tagResponse.data.listBlogTags.items);
      
    } catch (err) {
      console.error('Error fetching blogs:', err);
      setError('Failed to load blogs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortBlogs = () => {
    let filtered = [...blogs];
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(blog =>
        blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Filter by tag
    if (selectedTag) {
      filtered = filtered.filter(blog =>
        blog.tags?.some(tag => tag.id === selectedTag)
      );
    }
    
    // Sort blogs
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'latest':
          return new Date(b.publishedAt || b.createdAt) - new Date(a.publishedAt || a.createdAt);
        case 'oldest':
          return new Date(a.publishedAt || a.createdAt) - new Date(b.publishedAt || b.createdAt);
        case 'popular':
          return (b.viewCount || 0) - (a.viewCount || 0);
        case 'title':
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });
    
    setFilteredBlogs(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const truncateText = (text, maxLength = 150) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Pagination logic
  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = filteredBlogs.slice(indexOfFirstBlog, indexOfLastBlog);
  const totalPages = Math.ceil(filteredBlogs.length / blogsPerPage);

  if (loading) {
    return (
      <Container sx={{ py: 8 }}>
        <Skeleton height={48} width="40%" sx={{ mx: 'auto' }} />
        <Skeleton height={24} width="60%" sx={{ mx: 'auto', mb: 6 }} />
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' } }}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Box key={i}>
              <Skeleton variant="rectangular" height={190} sx={{ borderRadius: 2 }} />
              <Skeleton height={28} sx={{ mt: 1.5 }} />
              <Skeleton height={20} width="70%" />
            </Box>
          ))}
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
      <Seo
        title="Farm Journal: Notes from a Birbhum Farm"
        description="Stories from our organic farm in Birbhum, West Bengal — what we are sowing, what is in season, and how permaculture works through a Bengal year."
        path="/blog"
      />

      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
        <Typography
          variant="h2"
          component="h1"
          gutterBottom
          sx={{
            color: ink.primary,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
          }}
        >
          Farm Blog
        </Typography>
        <Box
          sx={{ width: 64, height: 3, mx: 'auto', mb: 3, borderRadius: 2, backgroundColor: brand.mustard }}
        />
        <Typography
          variant="h6"
          sx={{ color: ink.secondary, maxWidth: 640, mx: 'auto', fontWeight: 400, lineHeight: 1.7 }}
        >
          Stories from our organic farm, sustainable practices, seasonal updates, and agricultural insights
        </Typography>
      </Box>

      {/* Filters */}
      <Box
        sx={{
          mb: { xs: 4, md: 6 },
          p: { xs: 2, md: 3 },
          backgroundColor: surface.card,
          border: `1px solid ${line.subtle}`,
          borderRadius: 2,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search blogs"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by title or description..."
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Filter by Tag</InputLabel>
              <Select
                value={selectedTag}
                onChange={(e) => setSelectedTag(e.target.value)}
                label="Filter by Tag"
              >
                <MenuItem value="">All Tags</MenuItem>
                {tags.map((tag) => (
                  <MenuItem key={tag.id} value={tag.id}>
                    {tag.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Sort by</InputLabel>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                label="Sort by"
              >
                <MenuItem value="latest">Latest</MenuItem>
                <MenuItem value="oldest">Oldest</MenuItem>
                <MenuItem value="popular">Most Popular</MenuItem>
                <MenuItem value="title">Title A-Z</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <Typography variant="body2" sx={{ color: ink.secondary, fontWeight: 500 }}>
              {filteredBlogs.length} blog{filteredBlogs.length !== 1 ? 's' : ''} found
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Featured Blogs */}
      {blogs.some(blog => blog.isFeatured) && (
        <>
          <Typography variant="h4" component="h2" gutterBottom sx={sectionHeadingSx}>
            Featured Posts
          </Typography>
          <Grid container spacing={3} sx={{ mb: { xs: 5, md: 7 } }}>
            {blogs
              .filter(blog => blog.isFeatured)
              .sort((a, b) => (a.featuredOrder || 999) - (b.featuredOrder || 999))
              .slice(0, 3)
              .map((blog) => (
                <Grid item xs={12} md={4} key={blog.id}>
                  <Card sx={cardSx} onClick={() => navigate(`/blog/${blog.id}/${blog.slug}`)}>
                    <Box sx={{ position: 'relative' }}>
                      <CardMedia
                        component="img"
                        height="200"
                        image={blog.thumbnailDisplayUrl || '/api/placeholder/400/200'}
                        alt={blog.thumbnailImageAlt || blog.title}
                      />
                      <Chip
                        label="Featured"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 12,
                          left: 12,
                          // mustard is a surface here, with the dark ink that is
                          // contrast-checked against it (5.46:1)
                          backgroundColor: brand.mustard,
                          color: ink.onMustard,
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                        }}
                      />
                    </Box>
                    <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                      <Typography variant="h6" gutterBottom sx={cardTitleSx}>
                        {blog.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        paragraph
                        sx={{ color: ink.secondary, lineHeight: 1.65 }}
                      >
                        {truncateText(blog.description)}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarToday sx={metaIconSx} />
                          <Typography variant="caption" sx={metaSx}>
                            {formatDate(blog.publishedAt || blog.createdAt)}
                          </Typography>
                        </Box>
                        
                        {blog.viewCount && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Visibility sx={metaIconSx} />
                            <Typography variant="caption" sx={metaSx}>
                              {blog.viewCount} views
                            </Typography>
                          </Box>
                        )}

                        {blog.estimatedReadTime && (
                          <Typography variant="caption" sx={metaSx}>
                            {blog.estimatedReadTime} min read
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
          </Grid>
        </>
      )}

      {/* All Blogs */}
      <Typography variant="h4" component="h2" gutterBottom sx={sectionHeadingSx}>
        All Posts
      </Typography>
      
      {currentBlogs.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            backgroundColor: surface.sunken,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" sx={{ color: ink.secondary }}>
            No blogs found matching your criteria
          </Typography>
          <Button
            onClick={() => {
              setSearchTerm('');
              setSelectedTag('');
              setSortBy('latest');
            }}
            sx={{ mt: 2 }}
          >
            Clear Filters
          </Button>
        </Box>
      ) : (
        <>
          <Grid container spacing={3}>
            {currentBlogs.map((blog) => (
              <Grid item xs={12} sm={6} md={4} key={blog.id}>
                <Card sx={cardSx} onClick={() => navigate(`/blog/${blog.id}/${blog.slug}`)}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={blog.thumbnailDisplayUrl || '/api/placeholder/400/200'}
                    alt={blog.thumbnailImageAlt || blog.title}
                  />
                  <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                    <Typography variant="h6" gutterBottom sx={cardTitleSx}>
                      {blog.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      paragraph
                      sx={{ color: ink.secondary, lineHeight: 1.65 }}
                    >
                      {truncateText(blog.description)}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarToday sx={metaIconSx} />
                        <Typography variant="caption" sx={metaSx}>
                          {formatDate(blog.publishedAt || blog.createdAt)}
                        </Typography>
                      </Box>
                      
                      {blog.viewCount && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Visibility sx={metaIconSx} />
                          <Typography variant="caption" sx={metaSx}>
                            {blog.viewCount} views
                          </Typography>
                        </Box>
                      )}

                      {blog.estimatedReadTime && (
                        <Typography variant="caption" sx={metaSx}>
                          {blog.estimatedReadTime} min read
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                  <CardActions sx={{ px: 2.5, pb: 2, pt: 0 }}>
                    <Button
                      size="small"
                      sx={{
                        px: 0,
                        color: brand.mustardInk,
                        fontWeight: 600,
                        '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
                      }}
                    >
                      Read More
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(e, page) => setCurrentPage(page)}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default BlogPage;